import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pbAuthedFetch } from '../../authHeaders';

interface PipelineSource {
  id: string;
  key: string;
  name: string;
  kind: string;
  enabled: boolean;
  status: 'healthy' | 'stale' | 'failing' | 'never_run';
  ciCount: number;
  errorCount: number;
  totalRuns: number;
  lastRunAt: string | null;
  lastSuccessfulRunAt: string | null;
  daysSinceRun: number | null;
}

interface PipelineData {
  sources: PipelineSource[];
  summary: {
    total: number;
    healthy: number;
    stale: number;
    failing: number;
    neverRun: number;
    totalCIs: number;
  };
  as_of: string;
}

const statusPillClass: Record<string, string> = {
  healthy: 'pill--ok',
  stale: 'pill--warn',
  failing: 'pill--risk',
  never_run: '',
};

const statusLabels: Record<string, string> = {
  healthy: 'Healthy',
  stale: 'Stale',
  failing: 'Failing',
  never_run: 'Never Run',
};

export default function IngestPipeline() {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery<PipelineData>({
    queryKey: ['pipeline-status'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/pipeline/status');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const reingestMutation = useMutation({
    mutationFn: async (sourceKey: string) => {
      const res = await pbAuthedFetch(`/api/pipeline/ingest/${sourceKey}`, { method: 'POST' });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pipeline-status'] }),
  });

  if (isLoading) {
    return (
      <div className="box">
        <div className="box__body">
          <div className="page-loading" role="status" aria-live="polite">
            Loading pipeline status…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="box">
        <div className="box__body">
          <div className="state state--risk">
            <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3 className="state__title">Failed to load pipeline status</h3>
            <p className="state__body">
              Could not connect to the pipeline API. Please check your connection and try again.
            </p>
            <button className="btn btn--primary" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { sources, summary } = data;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--stg-s4)' }}>
        <button
          className="btn btn--primary"
          onClick={() => reingestMutation.mutate('__all__')}
          disabled={reingestMutation.isPending}
        >
          {reingestMutation.isPending ? 'Re-ingesting…' : 'Re-ingest All'}
        </button>
      </div>

      {summary.failing > 0 && (
        <div className="notice notice--risk">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <p className="notice__title">Pipeline Alert</p>
            <p className="notice__body">
              <strong>{summary.failing} source{summary.failing > 1 ? 's' : ''}</strong> {summary.failing > 1 ? 'are' : 'is'} currently failing.
              {summary.stale > 0 && (
                <> {summary.stale} source{summary.stale > 1 ? 's' : ''} {summary.stale > 1 ? 'are' : 'is'} stale.</>
              )}
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-4)', marginBottom: 'var(--sp-4)' }} role="group" aria-label="Pipeline summary">
        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Sources</p>
            <p className="ind__full">Total configured sources</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{summary.total}</p>
          </div>
          <p className="ind__reading">
            <i></i> <b>{summary.total}</b> sources tracked
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Healthy</p>
            <p className="ind__full">Sources reporting normally</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{summary.healthy}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--ok)' }}></i> <b style={{ color: 'var(--ok)' }}>On track</b> — reporting within expected window
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Stale</p>
            <p className="ind__full">Sources behind schedule</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{summary.stale}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--gold)' }}></i> <b style={{ color: 'var(--gold)' }}>Monitor</b> — not reported in expected window
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Failing</p>
            <p className="ind__full">Sources with errors</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{summary.failing}</p>
          </div>
          <p className="ind__reading">
            <i></i> <b>Action required</b> — ingestion errors detected
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">CIs</p>
            <p className="ind__full">Total configuration items</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{summary.totalCIs.toLocaleString()}</p>
          </div>
          <p className="ind__reading">
            <i></i> <b>{summary.totalCIs.toLocaleString()}</b> items in registry
          </p>
        </div>
      </div>

      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Flow</p>
            <h2 className="box__title">Pipeline Flow</h2>
          </div>
        </div>
        <div className="box__body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            {[
              { label: 'Sources', count: summary.total, icon: '📡' },
              { label: 'Ingest', count: summary.total, icon: '📥' },
              { label: 'Reconcile', count: summary.totalCIs, icon: '🔄' },
              { label: 'Registry', count: summary.totalCIs, icon: '📋' },
            ].map((stage, i) => (
              <div key={stage.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <div style={{
                  padding: 'var(--sp-3) var(--sp-5)',
                  background: 'var(--surface-sub)',
                  borderRadius: 'var(--r-box)',
                  textAlign: 'center',
                  minWidth: 100,
                }}>
                  <div style={{ fontSize: 'var(--text-xl)' }}>{stage.icon}</div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--ink)' }}>{stage.label}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)' }}>{stage.count}</div>
                </div>
                {i < 3 && <span style={{ fontSize: 'var(--text-xl)', color: 'var(--ink-45)' }}>→</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Sources</p>
            <h2 className="box__title">Pipeline Sources</h2>
          </div>
          <div className="box__head-actions">
            <span className="fact">{sources.length} source{sources.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="box__body">
          {sources.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--sp-4)' }}>
              {sources.map(source => (
                <div key={source.id} className="box" style={{ borderColor: source.status === 'failing' ? 'var(--risk)' : undefined }}>
                  <div className="box__head">
                    <div className="box__head-text">
                      <p className="box__eyebrow">{source.kind}</p>
                      <h3 className="box__title" style={{ fontSize: 'var(--text-base)' }}>{source.name}</h3>
                    </div>
                    <div className="box__head-actions">
                      <span className={`pill ${statusPillClass[source.status]}`}>
                        <i></i> {statusLabels[source.status]}
                      </span>
                    </div>
                  </div>
                  <div className="box__body">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)', fontSize: 'var(--text-sm)' }}>
                      <div>
                        <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '.06em' }}>CIs</span>
                        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{source.ciCount}</div>
                      </div>
                      <div>
                        <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Errors</span>
                        <div style={{ fontWeight: 600, color: source.errorCount > 0 ? 'var(--risk)' : 'var(--ink)' }}>{source.errorCount}</div>
                      </div>
                      <div>
                        <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Runs</span>
                        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{source.totalRuns}</div>
                      </div>
                      <div>
                        <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Last</span>
                        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{source.daysSinceRun !== null ? `${source.daysSinceRun}d ago` : 'Never'}</div>
                      </div>
                    </div>
                  </div>
                  <div className="box__foot">
                    <button
                      className="btn btn--sm"
                      onClick={() => reingestMutation.mutate(source.key)}
                      disabled={reingestMutation.isPending}
                    >
                      {reingestMutation.isPending ? 'Starting…' : 'Re-ingest'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <h3 className="state__title">No sources configured</h3>
              <p className="state__body">
                Visit <a href="/sources">Sources &amp; Ingest</a> to configure your first data source.
              </p>
              <a href="/sources" className="btn btn--primary">Configure Sources</a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
